import { AppointmentID } from "./Appointment";
import { CarID, CarTyresID } from "./Car";
import { COMMUNICATION_RESULT_STATUSES } from "./CustomerCommunication";
import { InterventionID } from "./Intervention";
import { QUESTION_RESULT_STATUSES, QuestionResultID } from "./QuestionResult";
import { TYRETEAM_CHANNELS } from "./Tyreteam";
import { User, UserID } from "./User";

export type TyreID = number;

export type TyreTeamSystemNumber = string;

export type TyreReplacementID = number;

export type VehicleTyreReplacementID = number;

export type VehicleTyreID = number;

export enum SEASONS {
  NoSeason = -1,
  AllSeason = 0,
  Winter = 1,
  Summer = 2,
  TruckSeason = 3
}

export enum TYRE_SOURCES {
  Unknown = 1,
  DBB = 2,
  TyreTeam = 3,
  TyreNet = 4,
  User = 5
}

export enum TYRE_POSITIONS {
  NotTyre = 0,
  CarFirst = 1,
  CarFrontLeft = 1,
  CarFrontRight = 2,
  CarRearLeft = 3,
  CarRearRight = 4,
  CarLast = 4,
  StorageFirst = 5,
  StorageFrontLeft = 5,
  StorageFrontRight = 6,
  StorageRearLeft = 7,
  StorageRearRight = 8,
  StorageLast = 8,
  CarSpare = 9,
  OnTruckFirst = 10,
  TruckLeft = 10,
  TruckRight = 11,
  TruckExteriorLeft = 12,
  TruckExteriorRight = 13,
  TruckInteriorLeft = 14,
  TruckInteriorRight = 15,
  TruckSpare = 16,
  OnTruckLast = 16
}

export enum TYRE_REPLACEMENT_SOURCES {
  Claire = 1,
  TyreTeam = 2,
  TyreNet = 3
}

export enum TYRE_LOCATIONS {
  UnknownLocation = 0,
  OnVehicle = 1,
  InStorage = 2,
  LastNotRemoved = 2,
  RemovedFromVehicle = 3,
  RemovedFromStorage = 4
}

export enum VEHICLE_TYRES_SOURCES {
  Mechanic = 1,
  DBB = 2,
  Scanner = 3,
  Replacement = 4
}

export interface Tyre {
  created_on: string;
  updated_on: string;
  id: TyreID; // Claire Tyre ID
  manufacturer: string; // Tyre manufacturer
  description: string; // Tyre description
  width: number; // Tyre width
  height: number; // Tyre height
  size: number; // Tyre size
  speed: string; // Tyre speed factor
  ean: string | null; // Tyre unique ID (EAN)
  run_flat_tire: boolean;
  carry_capacity: number;
  season: SEASONS; // Tyre season, refer to that enumeration for more details
  deleted: boolean; // Whether the tyre is deleted
  created_by_id: UserID | null; // For manually created tyres, User ID of the creator, nil otherwise
  created_source: TYRE_SOURCES; // Where the information for this Tyre originated from
  created_by?: User; // created by user
  three_peak_mountain_snow_flake?: boolean; // whether the tire has been tested and certified to provide reliable traction on snow, ice, and in other cold-weather conditions. Provided by third parties only. Usually denoted by 3PMSF.
  stock?: string; // Stock provided by third parties only. Not a number because TyreTeam cap values as '> 20'
  net_price?: number; // Net price. Provided by third parties only.
  gross_price?: number; // Gross price. Provided by third parties only.
  currency?: string; // Currency. Provided by some third parties only.
  tyre_team_system_number?: TyreTeamSystemNumber; // TyreTeam identifier
  tyre_team_delivery_method?: string; // TyreTeam delivery method.
  tyre_team_channel?: TYRETEAM_CHANNELS; // TyreTeam channel to order the tyre
  tyre_net_stock2?: number; // TyreNet stock available at supplier for delivery between 8-12h
  tyre_net_stock3?: number; // TyreNet stock available at supplier for delivery between 24-48h
}

export interface TyreReplacement {
  created_on: string;
  updated_on: string;
  tyre_replacement_id: TyreReplacementID; // Claire Tyre Replacement ID
  question_result_id: QuestionResultID; // Claire Question Result ID
  tyre_id: TyreID; // Claire Tyre ID
  price: number; // Price of this replacement tyre
  customer_answer: boolean; // Whether the mechanic can mount this replacement on the car or not
  remark: string; // Remarks from the receptionist or mechanic
  mechanic_fixed: boolean; // Whether the mechanic has mounted this replacement on the car or not
  selected: boolean; // Whether this replacement was selected by the customer to be mounted on the car or not
  profile: number; // Tyre tread profile entered by the mechanic after it was mounted on the car
  tyre_team_system_number: TyreTeamSystemNumber | null; // Reference to the TyreTeam Tyre ID
  tyre_team_delivery_method: string | null; // Delivery method of TyreTeam chosen, if there was one
  tyre_team_channel: TYRETEAM_CHANNELS | null; // Which Channel TyreTeam was queried through
  tyre_team_order_placed: boolean; // This replacement was ordered by the receptionist through TyreTeam
  tyre_net_delivery_method: number | null; // Delivery method of TyreNet chosen, if there was one
  tyre_net_order_placed: boolean; // This replacement was ordered by the receptionist through TyreNet
  price_reduction: number | null; // Reduction in the price
  discount: number | null; // Discount in the price
  tyre?: Tyre; // Tyre details for this replacement
  appointment_id?: AppointmentID; // Used to match the WS message to the appointment
}

export interface VehicleTyreReplacement {
  created_on: string;
  updated_on: string;
  id: VehicleTyreReplacementID;
  remark: string; // Remarks from the receptionist or mechanic
  selected: boolean; // Whether this replacement was selected by the customer to be mounted on the car or not
  mounted: boolean; // Whether this replacement was mounted on the vehicle/storage or not
  price: number; // Price of this replacement tyre
  discount: number | null; // Discount in the price
  net_margin_euro: number | null; // Margin applied from a tyre discount as a fixed euro amount
  net_margin_percent: number | null; // Margin applied from a tyre discount as a percentage
  tyre_team_system_number: TyreTeamSystemNumber | null; // Reference to the TyreTeam Tyre ID
  tyre_team_channel: TYRETEAM_CHANNELS | null; // Which Channel TyreTeam was queried through
  delivery_method: string | null; // Delivery method chosen, if there was one
  source: TYRE_REPLACEMENT_SOURCES; // This tyre was created from a specific source
  order_placed: boolean; // Order placed to a tyre supplier
  visible_to_customer: boolean; // Whether the replacement is visible to the customer in communication for selection
  tyre_id: TyreID; // Claire Tyre ID
  axle: number; // Axle of the truck tyre
  position: TYRE_POSITIONS; // Position of the tyre on the vehicle
  intervention_id: InterventionID | null; // Intervention Identifier that allows the mechanic to mount the tyres
  appointment_id: AppointmentID; // Which appointment these replacements are applied to
  tyre?: Tyre; // Tyre details for this replacement
  profile?: number; // Tyre tread profile entered by the mechanic after it was mounted on the car
  communication_result_status?: COMMUNICATION_RESULT_STATUSES; // The answer from the customer in a communication
}

export interface CarTyres {
  created_on: string;
  updated_on: string;
  id: CarTyresID; // Claire CarTyres ID
  front_left_tyre_id: TyreID | null;
  front_right_tyre_id: TyreID | null;
  rear_left_tyre_id: TyreID | null;
  rear_right_tyre_id: TyreID | null;
  fr_profile: number; // Front right tyre tread depth
  fl_profile: number; // Front left tyre tread depth
  rr_profile: number; // Rear right tyre tread depth
  rl_profile: number; // Rear left tyre tread depth
  fr_picture: string; // Front right tyre picture URL
  fl_picture: string; // Front left tyre picture URL
  rr_picture: string; // Rear right tyre picture URL
  rl_picture: string; // Rear left tyre picture URL
  location: TYRE_LOCATIONS; // Where the tyres are location, refer to that enumeration for more details
  dbb_state: string;
  dbb_fr_system_code: string;
  dbb_fl_system_code: string;
  dbb_rr_system_code: string;
  dbb_rl_system_code: string;
  dbb_fr_comment: string | null;
  dbb_fl_comment: string | null;
  dbb_rr_comment: string | null;
  dbb_rl_comment: string | null;
  dbb_location_name: string;
  dbb_sub_location_name: string;
  dbb_location_token: string;
  car_id: CarID;
  front_left_tyre?: Tyre; // Tyre object for the front left tyre, refer to that model for more details
  front_right_tyre?: Tyre; // Tyre object for the front right tyre, refer to that model for more details
  rear_left_tyre?: Tyre; // Tyre object for the rear left tyre, refer to that model for more details
  rear_right_tyre?: Tyre; // Tyre object for the rear right tyre, refer to that model for more details
}

export interface TruckTyres {
  created_on: string;
  updated_on: string;
  car_id: CarID;
  axle: number;
  position: TYRE_POSITIONS;
  tyre_id: TyreID;
  profile: number;
  picture: string;
  tyre?: Tyre;
}

export interface VehicleTyre {
  created_on: string;
  updated_on: string;
  id: VehicleTyreID;
  axle: number; // Tyre is fixed to this axle
  position: TYRE_POSITIONS; // Tyre has this position on the vehicle
  profile: number; // Tread depth of the tyre
  picture: string; // A picture of the tyre
  location: TYRE_LOCATIONS; // The tyre is on the vehicle or in storage
  dbb_state: string;
  dbb_system_code: string;
  dbb_comment: string;
  dbb_location_name: string;
  dbb_sub_location_name: string;
  dbb_location_token: string;
  question_result_remark: string; // Remark on the tyre
  question_result_status: QUESTION_RESULT_STATUSES | null; // The severity of the tyre given by a completed check
  source: VEHICLE_TYRES_SOURCES; // This tyre was created from a specific source
  tyre_id: TyreID | null;
  car_id: CarID;
  tyre?: Tyre; // Embedded Tyre
  replacements?: VehicleTyreReplacement[]; // Replacements to be offered to the customer to choose from, before being linked to an intervention and mounted
  updated_by: User | null; // User if created/updated by a mechanic
}
